import type { DocNetAgentProfile, DocNetDelegationStatus } from 'src/@types/docnet';
import DocNetBaseService from 'src/services/docnet/DocNetBaseService';
import { api } from 'src/pages/APIs';

export default class DocNetAgentProfileService extends DocNetBaseService {
  public async getProfiles(): Promise<DocNetAgentProfile[]> {
    const headers = await this.getHeaders();
    const response = await super.get(
      `${api.get_agents}?include_profiles=true`,
      { headers },
    );
    const agents = (response as any).agents || (response as any).items || [];
    return (agents as any[]).map((a: any) => this.toProfile(a));
  }

  private toProfile(agent: any): DocNetAgentProfile {
    return {
      agent: { id: agent.id, name: agent.name, description: agent.description, ...agent },
      role: agent.config?.role || 'specialist',
      skills_summary: (agent.skills || []).filter((s: any) => s.is_enabled).map((s: any) => s.skill_type),
      current_tasks: [],
      completed_task_count: 0,
      approval_mode: agent.config?.approval_mode || (agent.config?.require_approval ? 'critical' : 'none'),
      reports_to: agent.config?.reports_to,
      direct_reports: [],
      workload_percent: agent.heartbeat_status === 'working' ? 50 : 0,
      last_active_at: agent.last_heartbeat_at || agent.updated_at || agent.created_at || '',
      heartbeat_status: agent.heartbeat_status || null,
      last_heartbeat_at: agent.last_heartbeat_at || null,
      heartbeat_step: agent.heartbeat_step || null,
      performance: { success_rate: 0, avg_completion_hours: 0, tasks_per_week: 0, error_count: 0, streak_days: 0 },
    };
  }

  public async getProfileById(
    agentId: string,
  ): Promise<DocNetAgentProfile | undefined> {
    const headers = await this.getHeaders();
    try {
      const agent = await super.get(
        `${api.get_agent}${agentId}?include_profile=true`,
        { headers },
      );
      return agent ? this.toProfile(agent) : undefined;
    } catch {
      return undefined;
    }
  }

  public async updateAgentConfig(
    agentId: string,
    config: Record<string, unknown>,
  ): Promise<void> {
    const headers = await this.getHeaders();
    await super.put(`${api.get_agent}${agentId}`, { config }, { headers });
  }

  public async getAgentRuns(agentId: string): Promise<any[]> {
    const headers = await this.getHeaders();
    try {
      const response = await super.get(
        `${api.docnet_agent_runs}${agentId}/runs`,
        { headers },
      );
      return (response as any).runs || (response as any).items || [];
    } catch {
      return [];
    }
  }

  public async getDelegationStatus(): Promise<DocNetDelegationStatus[]> {
    const headers = await this.getHeaders();
    return (await super.get(api.docnet_delegation_status, {
      headers,
    })) as DocNetDelegationStatus[];
  }
}
